import { apiFetch, post } from "./api";
import type { PagedResult } from "./clients-accounts";

export type TaskType = "Admin" | "Badge" | "Production" | "Artwork";
export type TaskStatus = "Created" | "Assigned" | "InProgress" | "Completed" | "Cancelled";

export const TASK_TYPES: TaskType[] = ["Admin", "Badge", "Production", "Artwork"];
export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  Admin: "Admin", Badge: "Badge", Production: "Production", Artwork: "Artwork",
};
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  Created: "Unassigned", Assigned: "Assigned", InProgress: "In progress", Completed: "Completed", Cancelled: "Cancelled",
};
export const TASK_PRIORITY_LABELS: Record<number, string> = { 1: "Low", 2: "Normal", 3: "High", 4: "Urgent" };

export const taskCreateClaim = (t: TaskType): string => `tasks:create:${t.toLowerCase()}`;

export interface TaskListItemDto {
  id: number;
  taskType: TaskType;
  status: TaskStatus;
  title: string;
  priority: number;
  assignedEmployeeId: number | null;
  assignedEmployeeName: string | null;
  dueDateUtc: string | null;
  jobId: number | null;
  jobReferenceNo: string | null;
  clientName: string | null;
  createdOnUtc: string;
}

export interface TaskHistoryDto {
  id: number;
  action: string;
  previousStatus: string | null;
  newStatus: string | null;
  previousAssigneeId: number | null;
  newAssigneeId: number | null;
  reason: string | null;
  actorName: string;
  createdOnUtc: string;
}

export interface TaskDetailDto {
  id: number;
  taskType: TaskType;
  status: TaskStatus;
  title: string;
  description: string | null;
  priority: number;
  assignedEmployeeId: number | null;
  assignedEmployeeName: string | null;
  dueDateUtc: string | null;
  jobId: number | null;
  jobReferenceNo: string | null;
  branchId: number | null;
  followUpKind: string | null;
  quoteId: number | null;
  badgeQuantity: number | null;
  badgeStage: string | null;
  productionStage: string | null;
  expectedMinutes: number | null;
  jobArtworkId: number | null;
  artworkAction: string | null;
  startedOnUtc: string | null;
  completedOnUtc: string | null;
  completedByName: string | null;
  createdOnUtc: string;
  createdByName: string | null;
  modifiedOnUtc: string | null;
  rowVersionBase64: string | null;
  history: TaskHistoryDto[];
}

export interface ListTasksParams {
  type?: TaskType;
  status?: string;
  assigneeId?: number;
  jobId?: number;
  quoteId?: number;
  priority?: number;
  dueFrom?: string;
  dueTo?: string;
  q?: string;
  scope?: "mine" | "all";
  page?: number;
  pageSize?: number;
}

export interface CreateTaskRequest {
  taskType: TaskType;
  title: string;
  description?: string | null;
  priority?: number | null;
  assignedEmployeeId?: number | null;
  dueDateUtc?: string | null;
  jobId?: number | null;
  followUpKind?: string | null;
  quoteId?: number | null;
  badgeQuantity?: number | null;
  badgeStage?: string | null;
  productionStage?: string | null;
  expectedMinutes?: number | null;
  jobArtworkId?: number | null;
  artworkAction?: string | null;
}

export interface UpdateTaskRequest {
  title?: string | null;
  description?: string | null;
  priority?: number | null;
  dueDateUtc?: string | null;
  followUpKind?: string | null;
  badgeQuantity?: number | null;
  badgeStage?: string | null;
  productionStage?: string | null;
  expectedMinutes?: number | null;
  artworkAction?: string | null;
  rowVersionBase64: string;
}

export interface TaskTypeCountDto { taskType: TaskType; openCount: number; overdueCount: number; }
export interface TaskPriorityCountDto { priority: number; openCount: number; }
export interface TaskCompletionPointDto { date: string; count: number; }
export interface TaskSummaryDto { totalOpen: number; totalOverdue: number; byType: TaskTypeCountDto[]; byPriority: TaskPriorityCountDto[]; completionSeries: TaskCompletionPointDto[]; }
export interface BulkRowResult { id: number; success: boolean; error: string | null; }
export interface BulkResult { results: BulkRowResult[]; }

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}

export const tasksApi = {
  list: (params: ListTasksParams = {}): Promise<PagedResult<TaskListItemDto>> => {
    const search = qs(params as Record<string, string | number | boolean | undefined | null>);
    return apiFetch<PagedResult<TaskListItemDto>>(`/api/tasks${search ? `?${search}` : ""}`);
  },
  get: (id: number): Promise<TaskDetailDto> => apiFetch<TaskDetailDto>(`/api/tasks/${id}`),
  create: (body: CreateTaskRequest): Promise<TaskDetailDto> =>
    apiFetch<TaskDetailDto>("/api/tasks", { method: "POST", body: JSON.stringify(body) }),
  patch: (id: number, body: UpdateTaskRequest): Promise<TaskDetailDto> =>
    apiFetch<TaskDetailDto>(`/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: number): Promise<void> => apiFetch<void>(`/api/tasks/${id}`, { method: "DELETE" }),
  start: (id: number): Promise<TaskDetailDto> => post<TaskDetailDto>(`/api/tasks/${id}/start`, {}),
  complete: (id: number, comment?: string): Promise<TaskDetailDto> => post<TaskDetailDto>(`/api/tasks/${id}/complete`, { comment }),
  cancel: (id: number, reason: string): Promise<TaskDetailDto> => post<TaskDetailDto>(`/api/tasks/${id}/cancel`, { reason }),
  reopen: (id: number): Promise<TaskDetailDto> => post<TaskDetailDto>(`/api/tasks/${id}/reopen`, {}),
  reassign: (id: number, assigneeId: number, reason?: string): Promise<TaskDetailDto> =>
    post<TaskDetailDto>(`/api/tasks/${id}/reassign`, { assigneeId, reason }),
  comment: (id: number, text: string): Promise<TaskDetailDto> => post<TaskDetailDto>(`/api/tasks/${id}/comment`, { text }),
  summary: (scope: "mine" | "all" = "mine"): Promise<TaskSummaryDto> => apiFetch<TaskSummaryDto>(`/api/tasks/summary?scope=${scope}`),
  bulkComplete: (ids: number[], comment?: string): Promise<BulkResult> => post<BulkResult>("/api/tasks/bulk-complete", { ids, comment }),
  bulkReassign: (ids: number[], assigneeId: number, reason?: string): Promise<BulkResult> => post<BulkResult>("/api/tasks/bulk-reassign", { ids, assigneeId, reason }),
};
